import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  BanknotesIcon,
  BellIcon,
  DocumentTextIcon,
  QuestionMarkCircleIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useClubSettings } from '../../contexts/ClubSettingsContext';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useSportDoctorWarningDays } from '../../utils/playerMedical';
import { useFamilyAgenda } from '../../components/family/useFamilyAgenda';
import { Group, LoadingRows, PageTitle, Row, SectionLabel } from '../../components/family/ui';
import { dayOffset, formatFt, isoDate } from '../../components/family/format';
import InstallAppCard from '../../components/ui/InstallAppCard';

/**
 * A "Profil" fül: a gyerek(ek) adatlapja, tagdíj, dokumentumok, értesítések és
 * fiók - minden, ami nem a programról szól.
 */
const FamilyMe: React.FC = () => {
  usePageTitle('Profil');
  const { user, logout } = useAuth();
  const { settings } = useClubSettings();
  const navigate = useNavigate();
  const warningDays = useSportDoctorWarningDays();
  // Csak a játékosok kellenek; az esemény-időszak egyetlen nap
  const [today] = useState(() => isoDate(new Date()));
  const { agenda } = useFamilyAgenda(today, today);

  const players = agenda?.players ?? [];
  const onlySelf = players.length === 1 && players[0].isSelf;
  const unpaid = players.reduce((sum, p) => sum + p.unpaidFeeAmount, 0);
  const feesEnabled = settings?.membershipFeesEnabled ?? false;

  const doctorText = (validity?: string | null) => {
    if (!validity) return 'Sportorvosi: nincs megadva';
    const days = dayOffset(validity);
    const date = new Date(validity).toLocaleDateString('hu-HU');
    if (days < 0) return `Sportorvosi lejárt (${date})`;
    if (days <= warningDays) return `Sportorvosi hamarosan lejár (${date})`;
    return `Sportorvosi érvényes: ${date}`;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div>
      <PageTitle title={user?.name ?? 'Profil'} subtitle={user?.email} />

      <SectionLabel>{onlySelf ? 'Adataim' : players.length > 1 ? 'Gyerekeim' : 'Gyermekem'}</SectionLabel>
      {!agenda ? (
        <LoadingRows rows={1} />
      ) : players.length === 0 ? (
        <p className="rounded-2xl bg-white p-4 text-[15px] text-gray-600 ring-1 ring-black/5">
          Még nincs játékos a fiókodhoz kötve. Szólj az edzőnek.
        </p>
      ) : (
        <Group>
          {players.map((p) => (
            <Row
              key={p.playerId}
              to={`/me/players/${p.playerId}`}
              icon={UserIcon}
              title={p.name}
              subtitle={`${p.ageGroup} · ${doctorText(p.sportDoctorValidity)}`}
            />
          ))}
        </Group>
      )}

      <SectionLabel>Klub</SectionLabel>
      <Group>
        {feesEnabled && (
          <Row
            to="/me/fees"
            icon={BanknotesIcon}
            title="Tagdíj"
            value={unpaid > 0 ? <span className="font-medium text-red-600">{formatFt(unpaid)}</span> : 'Nincs tartozás'}
          />
        )}
        <Row to="/me/documents" icon={DocumentTextIcon} title="Dokumentumok" subtitle="Igazolások, kikérők, klubdokumentumok" />
      </Group>

      <SectionLabel>Beállítások</SectionLabel>
      <Group>
        <Row to="/me/account" icon={BellIcon} title="Fiók és értesítések" subtitle="Értesítések, naptár a telefonodba, jelszó" />
        <Row to="/help" icon={QuestionMarkCircleIcon} title="Súgó" />
      </Group>

      <div className="mt-6">
        <InstallAppCard />
      </div>

      <Group className="mt-6">
        <Row onClick={handleLogout} icon={ArrowRightOnRectangleIcon} title="Kijelentkezés" tone="danger" chevron={false} />
      </Group>
    </div>
  );
};

export default FamilyMe;
